/**
 * FactsTab.tsx — `facts[]` (ADR-084 D1), the agentic editor's Facts tab
 * (design doc §Facts tab, E12-S3a). A table (order via the shared
 * ReorderableList, label, question guidance, required, key, builtin badge)
 * plus a select-to-edit row editor for the full `FactDef` — same pattern the
 * design doc specifies for Scripts' step editor, for the same reason
 * (format/pattern/values is a heavier sub-form than fits inline).
 *
 * A `builtin=true` row's `key`/`format`/`pattern`/`values` are locked
 * (engineering-owned, D4 rule 5) and its remove control is ABSENT, not
 * disabled-and-unexplained (D1 comment: "tenant may reorder, not delete").
 * `label`/`question_guidance`/`required` stay editable on every row.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AGENT_CONFIG_MAX_LENGTHS,
  FACT_DEF_FORMATS,
  type FactDef,
  type SkillVersionFieldError,
} from '../../api/types';
import { CounterField, textActionClass, textActionDangerClass } from './agenticFieldHelpers';
import { ReorderableList } from './ReorderableList';
import { Badge } from '../../components/Badge';
import { inputClass } from '../../components/Field';

export interface FactsTabProps {
  facts: FactDef[];
  onChange: (next: FactDef[]) => void;
  disabled?: boolean;
  fieldErrors: SkillVersionFieldError[];
}

const errorFor = (fieldErrors: SkillVersionFieldError[], path: string): string | undefined =>
  fieldErrors.find((error) => error.field_path === path)?.reason;

let newFactSeq = 0;

export const FactsTab = ({ facts, onChange, disabled = undefined, fieldErrors }: FactsTabProps) => {
  const { t } = useTranslation();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const updateFact = (key: string, patch: Partial<FactDef>) =>
    onChange(facts.map((fact) => (fact.key === key ? { ...fact, ...patch } : fact)));

  const removeFact = (key: string) => {
    onChange(facts.filter((fact) => fact.key !== key));
    if (selectedKey === key) setSelectedKey(null);
  };

  const addFact = () => {
    newFactSeq += 1;
    const key = `novo_fato_${newFactSeq}`;
    onChange([
      ...facts,
      {
        key,
        label: '',
        question_guidance: '',
        format: 'text',
        pattern: null,
        values: null,
        description: null,
        required: true,
        builtin: false,
      },
    ]);
    setSelectedKey(key);
  };

  const selected = facts.find((fact) => fact.key === selectedKey) ?? null;

  return (
    <section aria-labelledby="agentic-facts-heading" className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 id="agentic-facts-heading" className="text-h3 text-ink-1">
          {t('config.editAgentic.facts.heading')}
        </h3>
        <button
          type="button"
          data-testid="agentic-facts-add"
          disabled={disabled}
          onClick={addFact}
          className={textActionClass}
        >
          {t('config.editAgentic.facts.add')}
        </button>
      </div>

      {facts.length === 0 ? (
        <p data-testid="agentic-facts-empty" className="text-body text-ink-2">
          {t('config.editAgentic.facts.empty')}
        </p>
      ) : (
        <ReorderableList
          items={facts}
          getId={(fact) => fact.key}
          onReorder={onChange}
          ariaLabel={t('config.editAgentic.facts.heading')}
          disabled={disabled}
          renderItem={(fact) => (
            <>
              <button
                type="button"
                data-testid={`agentic-fact-${fact.key}-select`}
                onClick={() => setSelectedKey(fact.key)}
                className="flex min-h-6 flex-1 flex-wrap items-center gap-3 text-left"
              >
                <span className="text-body text-ink-1">{fact.label || fact.key}</span>
                <span className={`text-meta ${fact.required ? 'text-ink-2' : 'text-ink-3'}`}>
                  {fact.required
                    ? t('config.editAgentic.facts.required')
                    : t('config.editAgentic.facts.optional')}
                </span>
                <span
                  data-testid={`agentic-fact-${fact.key}-key`}
                  className="font-mono text-meta text-ink-3"
                >
                  {fact.key}
                </span>
                {fact.builtin ? (
                  <Badge tone="neutral" data-testid={`agentic-fact-${fact.key}-builtin`}>
                    {t('config.editAgentic.facts.builtin')}
                  </Badge>
                ) : null}
              </button>
              {/* Sibling, not child, of the select button: nested interactive
                  controls are invalid ARIA (WCAG 4.1.2). */}
              {fact.builtin ? null : (
                <button
                  type="button"
                  data-testid={`agentic-fact-${fact.key}-remove`}
                  aria-label={t('config.editAgentic.facts.remove')}
                  disabled={disabled}
                  onClick={() => removeFact(fact.key)}
                  className={textActionDangerClass}
                >
                  {t('config.editAgentic.facts.remove')}
                </button>
              )}
            </>
          )}
        />
      )}

      {selected != null ? (
        <div
          aria-live="polite"
          data-testid={`agentic-fact-${selected.key}-editor`}
          className="flex flex-col gap-3 rounded-lg bg-surface-2 p-4 ring-1 ring-inset ring-ink-3/10"
        >
          <h4 className="text-body font-medium text-ink-1">
            {t('config.editAgentic.facts.editingHeading', {
              label: selected.label || selected.key,
            })}
          </h4>
          <CounterField
            id={`agentic-fact-${selected.key}-label`}
            label={t('config.editAgentic.facts.labelField')}
            value={selected.label}
            maxLength={AGENT_CONFIG_MAX_LENGTHS.factLabel}
            onChange={(next) => updateFact(selected.key, { label: next })}
            disabled={disabled}
            error={errorFor(fieldErrors, `facts.${selected.key}.label`)}
          />
          <CounterField
            id={`agentic-fact-${selected.key}-question-guidance`}
            label={t('config.editAgentic.facts.questionGuidance')}
            value={selected.question_guidance}
            maxLength={AGENT_CONFIG_MAX_LENGTHS.factQuestionGuidance}
            onChange={(next) => updateFact(selected.key, { question_guidance: next })}
            disabled={disabled}
            multiline
            rows={3}
            error={errorFor(fieldErrors, `facts.${selected.key}.question_guidance`)}
          />
          <label
            htmlFor={`agentic-fact-${selected.key}-required`}
            className="flex items-center gap-2 text-meta text-ink-2"
          >
            <input
              type="checkbox"
              id={`agentic-fact-${selected.key}-required`}
              data-testid={`agentic-fact-${selected.key}-required`}
              checked={selected.required}
              disabled={disabled}
              onChange={(event) => updateFact(selected.key, { required: event.target.checked })}
            />
            {t('config.editAgentic.facts.required')}
          </label>

          <div className="flex flex-col gap-1">
            <label
              htmlFor={`agentic-fact-${selected.key}-format`}
              className="text-meta font-medium text-ink-2"
            >
              {t('config.editAgentic.facts.format')}
            </label>
            {selected.builtin ? (
              <p
                data-testid={`agentic-fact-${selected.key}-format-readonly`}
                className="text-body text-ink-2"
              >
                {selected.format}
              </p>
            ) : (
              <select
                id={`agentic-fact-${selected.key}-format`}
                data-testid={`agentic-fact-${selected.key}-format`}
                value={selected.format}
                disabled={disabled}
                onChange={(event) =>
                  updateFact(selected.key, { format: event.target.value as FactDef['format'] })
                }
                className={inputClass}
              >
                {FACT_DEF_FORMATS.map((format) => (
                  <option key={format} value={format}>
                    {format}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor={`agentic-fact-${selected.key}-pattern`}
              className="text-meta font-medium text-ink-2"
            >
              {t('config.editAgentic.facts.pattern')}
            </label>
            {selected.builtin ? (
              <p
                data-testid={`agentic-fact-${selected.key}-pattern-readonly`}
                className="font-mono text-body text-ink-2"
              >
                {selected.pattern ?? '—'}
              </p>
            ) : (
              <input
                id={`agentic-fact-${selected.key}-pattern`}
                data-testid={`agentic-fact-${selected.key}-pattern`}
                type="text"
                value={selected.pattern ?? ''}
                disabled={disabled}
                onChange={(event) =>
                  updateFact(selected.key, { pattern: event.target.value || null })
                }
                className={`${inputClass} font-mono`}
              />
            )}
            {errorFor(fieldErrors, `facts.${selected.key}.pattern`) != null ? (
              <p
                role="alert"
                data-testid={`agentic-fact-${selected.key}-pattern-error`}
                className="text-meta text-status-down"
              >
                {errorFor(fieldErrors, `facts.${selected.key}.pattern`)}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor={`agentic-fact-${selected.key}-values`}
              className="text-meta font-medium text-ink-2"
            >
              {t('config.editAgentic.facts.values')}
            </label>
            {selected.builtin ? (
              <p
                data-testid={`agentic-fact-${selected.key}-values-readonly`}
                className="font-mono text-body text-ink-2"
              >
                {selected.values?.join(', ') ?? '—'}
              </p>
            ) : (
              <input
                id={`agentic-fact-${selected.key}-values`}
                data-testid={`agentic-fact-${selected.key}-values`}
                type="text"
                value={selected.values?.join(', ') ?? ''}
                disabled={disabled}
                onChange={(event) =>
                  updateFact(selected.key, {
                    values:
                      event.target.value.trim() === ''
                        ? null
                        : event.target.value.split(',').map((v) => v.trim()),
                  })
                }
                className={`${inputClass} font-mono`}
              />
            )}
            {errorFor(fieldErrors, `facts.${selected.key}.values`) != null ? (
              <p
                role="alert"
                data-testid={`agentic-fact-${selected.key}-values-error`}
                className="text-meta text-status-down"
              >
                {errorFor(fieldErrors, `facts.${selected.key}.values`)}
              </p>
            ) : null}
          </div>

          <button
            type="button"
            data-testid={`agentic-fact-${selected.key}-close`}
            onClick={() => setSelectedKey(null)}
            className={`self-end ${textActionClass}`}
          >
            {t('config.editAgentic.facts.closeEditing')}
          </button>
        </div>
      ) : null}
    </section>
  );
};
